import { DUNGEON, type HeroClass } from '@somnhold/content';
import type { BattleState, Hero } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { isTouch } from '../settings.ts';
import { h, setFill, setHidden, setStyle, setText } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

const COMPACT_KEY = 'somnhold.partyCompact';

export const CLASS_ICON: Record<HeroClass, string> = { warrior: ICONS.sword, mage: ICONS.staff, ranger: ICONS.bow, artificer: ICONS.wrench, cleric: ICONS.sunStaff, barbarian: ICONS.axe, paladin: ICONS.mace, druid: ICONS.antlers };

/**
 * The party at a glance: each hero's class, name, health and state — live, a ghost, a hired
 * mercenary, down (and when it gets back up, or how close an ally is to picking it up in the crypt).
 * On a mouse, a big party folds into a grid of small chips. On touch, it is a thin row of chips that
 * taps go straight through (they were opening and closing the list instead of moving or shooting); it
 * opens into the full, touchable list only while a heal is waiting for its target.
 */
export class PartyFrames {
  readonly el: HTMLElement;
  private list = h('div.pf-list');
  private head = h('button.pf-head', { type: 'button' });
  private frames = new Map<string, { root: HTMLElement; fill: HTMLElement; hp: HTMLElement; rev: HTMLElement; tag: HTMLElement; secs: HTMLElement }>();
  private compact: boolean | null = null;
  private picking = false;
  /** Touching a hero's frame (a cleric heals it); null = frames are read-only. */
  onPick: ((heroId: number) => void) | null = null;

  constructor(
    private me: string,
    private names: Record<string, string>,
    cls = 'dg-party',
  ) {
    try {
      const saved = localStorage.getItem(COMPACT_KEY);
      if (saved !== null) this.compact = saved === '1';
    } catch {
      /* storage off: decide by size */
    }
    this.head.addEventListener('click', () => {
      this.compact = !this.el.classList.contains('compact');
      try {
        localStorage.setItem(COMPACT_KEY, this.compact ? '1' : '0');
      } catch {
        /* not remembered */
      }
      this.el.classList.toggle('compact', this.compact);
    });
    this.el = h(`div.${cls}`, {}, this.head, this.list);
  }

  nameOf(hero: Hero): string {
    if (hero.control === 'merc') return t('party.merc');
    return this.names[hero.pid] || hero.name || t(`class.${hero.cls}`);
  }

  /** A call for help: the caller's frame beats for a while. */
  flag(pid: string, ms = 8000): void {
    const f = this.frames.get(pid);
    if (!f) return;
    f.root.classList.remove('sos');
    void f.root.offsetWidth;
    f.root.classList.add('sos');
    window.clearTimeout(Number(f.root.dataset.sos ?? 0));
    f.root.dataset.sos = String(window.setTimeout(() => f.root.classList.remove('sos'), ms));
  }

  /** Frames light up while a heal is waiting for its target. */
  choosing(on: boolean): void {
    this.picking = on;
    this.el.classList.toggle('choosing', on);
    this.el.classList.toggle('strip', isTouch() && !on);
  }

  update(s: BattleState): void {
    for (const hero of s.heroes) this.frame(s, hero);
    const n = s.heroes.length;
    setText(this.head, t('party.title', { n }));
    setHidden(this.head, n < 2 || isTouch());
    // Folded by default on phones once the party gets big; whatever the player picked wins, at any size.
    const compact = n >= 2 && !isTouch() && !!this.compact;
    this.el.classList.toggle('compact', compact);
    this.el.classList.toggle('strip', isTouch() && !this.picking);
  }

  private frame(s: BattleState, hero: Hero): void {
    let f = this.frames.get(hero.pid);
    if (!f) {
      const fill = h('i');
      const hp = h('span.dg-hp');
      const rev = h('span.dg-rev');
      const tag = h('span.dg-kind');
      const secs = h('span.dg-secs');
      const root = h(`div.dg-member${hero.pid === this.me ? '.me' : ''}`, { title: this.nameOf(hero) }, h('span.dg-cls', { html: CLASS_ICON[hero.cls] }, secs), h('div.dg-mid', {}, h('b', {}, this.nameOf(hero), tag), h('div.bar.green', {}, fill)), hp, rev);
      // You first, then whoever plays live, then ghosts, then hired hands.
      root.style.order = String(hero.pid === this.me ? 0 : hero.control === 'live' ? 1 : hero.control === 'merc' ? 3 : 2);
      root.addEventListener('pointerdown', (ev) => {
        if (!this.onPick) return;
        ev.stopPropagation();
        this.onPick(hero.id);
        root.classList.remove('picked');
        void root.offsetWidth;
        root.classList.add('picked');
      });
      this.list.append(root);
      f = { root, fill, hp, rev, tag, secs };
      this.frames.set(hero.pid, f);
    }
    const down = hero.downTicks > 0;
    const crypt = s.mode === 'dungeon';
    // Orla (the dragon's fight, La Quemadura done) fights alongside, alive: not a spectre. So do the night of the bell's
    // allies (El Párpado 4), at the bastion: «aliado» (Gaspar is the one spectre among them, and says so).
    const night = s.mode === 'wave' && !!s.rules?.night && hero.pid.startsWith('npc:');
    const orla = hero.pid === 'npc:orla' && !night;
    const ghost = hero.control === 'ghost' && !orla && !night;
    f.root.classList.toggle('down', down);
    f.root.classList.toggle('pickable', !!this.onPick);
    f.root.classList.toggle('hurt', !down && hero.hp * 2 < hero.stats.maxHp);
    // Well enough not to need a heal: dimmed in the strip, so the ones who do stand out.
    f.root.classList.toggle('well', !down && hero.hp * 4 >= hero.stats.maxHp * 3);
    f.root.classList.toggle('ghost', ghost && !crypt);
    f.root.classList.toggle('merc', hero.control === 'merc');
    f.root.classList.toggle('away', crypt && hero.control !== 'live' && hero.control !== 'merc');
    setText(f.tag, hero.control === 'merc' ? ` · ${t(`class.${hero.cls}`)}` : night ? ` · ${t(hero.pid === 'npc:gaspar' ? 'party.ghost' : 'party.ally')}` : orla ? ` · ${t('party.wayfarer')}` : ghost && !crypt ? ` · ${t('party.ghost')}` : '');
    setFill(f.fill, ((100 * Math.max(0, hero.hp)) / hero.stats.maxHp) / 100);
    // Eaten by the dragon of the Burn: out for good, no countdown (docs/UNICAS.md §4.4).
    const eaten = !!s.dragon?.eaten.includes(hero.pid);
    f.root.classList.toggle('eaten', eaten);
    // Down: in the crypt until an ally comes; elsewhere back on its feet after a countdown.
    setText(f.hp, eaten ? t('party.eaten') : down ? (crypt ? t('dungeon.down') : t('party.backIn', { n: Math.ceil(hero.downTicks / 12) })) : String(hero.hp));
    // In the strip a fallen hero's chip shows just the seconds left (an eaten one, its mark).
    setText(f.secs, eaten ? '✕' : down && !crypt ? String(Math.ceil(hero.downTicks / 12)) : '');
    const p = down && crypt ? Math.min(1, (hero.revive ?? 0) / DUNGEON.reviveTicks) : 0;
    setStyle(f.rev, '--p', `${Math.round(p * 100)}%`);
  }
}
